import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Input, VStack, HStack, Text, Heading, Stack } from "@chakra-ui/react";

export default function AgentDashboard({ onLogout }) {
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([
    { id: 1, from: "user", text: "Swiftchang support" }
  ]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const ws = useRef(null);
  const shouldReconnect = useRef(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";
  const WS_BASE_URL = API_BASE_URL.replace(/^http/, "ws");

  const formatTimestamp = (value) => {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  useEffect(() => {
    if (!selectedEmail || !validateEmail(selectedEmail)) return;
    async function fetchHistory() {
      try {
        const normalizedEmail = selectedEmail.trim().toLowerCase();
        const res = await fetch(`${API_BASE_URL}/api/chat_history/?email=${encodeURIComponent(normalizedEmail)}`);
        const data = await res.json();
        if (data.status === 'success') {
          setChatMessages(
            data.messages.map(msg => ({
              id: msg.id,
              from: msg.sender === "user" ? "user" : "support",
              text: msg.message,
              email: msg.email,
              timestamp: formatTimestamp(msg.timestamp)
            }))
          );
        }
      } catch (e) {
        // fallback: do nothing
      }
    }
    fetchHistory();
    const normalizedEmail = selectedEmail.trim().toLowerCase();
    shouldReconnect.current = true;
    ws.current = new window.WebSocket(`${WS_BASE_URL}/ws/livechat/?email=${encodeURIComponent(normalizedEmail)}`);
    ws.current.onopen = () => {};
    ws.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.sender === "user" && data.email) {
        setChatMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            from: "user",
            text: data.message,
            email: data.email,
            timestamp: formatTimestamp(data.timestamp) || formatTimestamp(new Date())
          }
        ]);
      } else {
        setChatMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            from: data.sender === "user" ? "user" : "support",
            text: data.message,
            timestamp: formatTimestamp(data.timestamp) || formatTimestamp(new Date())
          }
        ]);
      }
    };
    ws.current.onclose = () => {
      if (shouldReconnect.current) {
        setTimeout(() => {
          if (validateEmail(selectedEmail)) {
            const nextEmail = selectedEmail.trim().toLowerCase();
            ws.current = new window.WebSocket(`${WS_BASE_URL}/ws/livechat/?email=${encodeURIComponent(nextEmail)}`);
          }
        }, 500);
      }
    };
    ws.current.onerror = () => {
      if (ws.current) {
        ws.current.close();
      }
    };
    return () => {
      shouldReconnect.current = false;
      ws.current && ws.current.close();
    };
  }, [selectedEmail]);

  function validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  const handleSendMessage = () => {
    const message = chatInput.trim();
    if (!message || !ws.current || ws.current.readyState !== 1) return;
    if (!validateEmail(selectedEmail)) return;
    const normalizedEmail = selectedEmail.trim().toLowerCase();
    const timestamp = formatTimestamp(new Date());
    ws.current.send(JSON.stringify({ message, sender: "support", email: normalizedEmail, timestamp }));
    setChatInput("");
  };

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="transparent">
      <Box maxW="600px" w="100%" p={6} borderWidth={1} borderRadius="lg" boxShadow="md" bg="white">
        <HStack justify="space-between" mb={2}>
          <Heading size="md" textAlign="center">Agent Live Chat Dashboard</Heading>
          <Button size="sm" colorScheme="red" variant="outline" onClick={onLogout}>Logout</Button>
        </HStack>
        <VStack spacing={4} align="stretch">
          <Input
            placeholder="Enter user email to view chat"
            bg="gray.50"
            value={selectedEmail}
            onChange={e => setSelectedEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            isInvalid={emailTouched && !validateEmail(selectedEmail)}
            focusBorderColor="green.600"
            mb={2}
            required
          />
          <Box minH="300px" maxH="400px" overflowY="auto" borderWidth={1} borderRadius="md" p={3} bg="gray.50">
            <VStack spacing={3} align="stretch">
              {chatMessages.map((message, idx) => (
                <HStack key={message.id + idx} justify={message.from === "support" ? "flex-end" : "flex-start"}>
                  <Box
                    bg={message.from === "support" ? "green.600" : "gray.200"}
                    color={message.from === "support" ? "white" : "gray.700"}
                    px={4}
                    py={2}
                    borderRadius="lg"
                    maxW="75%"
                  >
                    {message.email && (
                      <Text fontSize="xs" color="blue.600" fontWeight="bold" mb={1}>
                        Email: {message.email}
                      </Text>
                    )}
                    <Text fontSize="sm" whiteSpace="pre-line">{message.text}</Text>
                    {message.timestamp && (
                      <Text fontSize="xs" color="gray.400" mt={1} textAlign="right">{message.timestamp}</Text>
                    )}
                  </Box>
                </HStack>
              ))}
            </VStack>
          </Box>
          <Stack direction={{ base: "column", md: "row" }} spacing={3}>
            <Input
              placeholder="Type your message..."
              bg="gray.50"
              value={chatInput}
              onChange={(event) => setChatInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSendMessage();
                }
              }}
              isDisabled={!validateEmail(selectedEmail)}
            />
            <Button bg="green.600" color="white" _hover={{ bg: "green.700" }} onClick={handleSendMessage} isDisabled={!validateEmail(selectedEmail)}>
              Send
            </Button>
          </Stack>
        </VStack>
      </Box>
    </Box>
  );
}
